import {
  AddLoadTo,
  ComponentDesign,
  LoadDirection,
  LoadMemberType,
  loadMemberTypes,
  LoadType,
  ManualLoadDefinition,
  ManualLoadSettings,
} from '@paragon/contract-library';

import { deleteItems } from '../../store/actions/deletionActions';
import { addManualLoads } from '../../store/actions/insertActions';
import { calculateActiveComponentMembersMinMax } from '../../store/selectors/boundsSelectors';
import { getLoadCasesForActiveComponentDesign } from '../../store/selectors/getLoadCasesForActiveComponentDesignSelector';
import { getLoadCasesForActiveComponentDesignSorted } from '../../store/selectors/getLoadCasesForActiveComponentDesignSortedSelector';
import { openTruss, requireString, requireStringArray, waitForAppToSettle } from './helpers';
import { defineTool, ToolError } from './types';

const LOAD_TYPES: readonly LoadType[] = ['Dead', 'LiveRoof', 'LiveFloor', 'Snow', 'Wind', 'Rain', 'Seismic', 'Person'];
const LOAD_DIRECTIONS: readonly LoadDirection[] = [
  'GlobalDown',
  'GlobalUp',
  'GlobalRight',
  'GlobalLeft',
  'NormalDown',
  'NormalUp',
  'ParallelRight',
  'ParallelLeft',
];

export interface LoadInput {
  readonly kind: 'point' | 'distributed';
  readonly loadType: LoadType;
  readonly member?: LoadMemberType;
  readonly at?: number;
  readonly magnitude?: number;
  readonly from?: number;
  readonly to?: number;
  readonly startMagnitude?: number;
  readonly endMagnitude?: number;
  readonly unit?: 'plf' | 'psf';
  readonly direction?: LoadDirection;
  readonly loadCase?: string;
  readonly stationaryOnly?: boolean;
  readonly name?: string;
}

interface Bounds {
  readonly minX: number;
  readonly maxX: number;
}

function requireFinite(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new ToolError(`${label} must be a number.`);
  return value;
}

function requireOnTruss(x: number, bounds: Bounds, label: string) {
  if (x < bounds.minX - 1e-6 || x > bounds.maxX + 1e-6)
    throw new ToolError(
      `${label} (${x}) is off the truss, which runs from x = ${bounds.minX} to ${bounds.maxX} inches.`,
    );
}

/**
 * Builds the manual load the app's Add Load panel would, defined along the truss's x axis as the panel defines it.
 */
export function buildManualLoad(input: LoadInput, bounds: Bounds, index = 0): ManualLoadSettings {
  const label = `loads[${index}]`;
  if (!LOAD_TYPES.includes(input.loadType))
    throw new ToolError(`${label}.loadType must be one of ${LOAD_TYPES.join(', ')}.`);
  const member = input.member ?? 'TopChord';
  if (!loadMemberTypes.includes(member))
    throw new ToolError(`${label}.member must be one of ${loadMemberTypes.join(', ')}.`);
  const direction = input.direction ?? 'GlobalDown';
  if (!LOAD_DIRECTIONS.includes(direction))
    throw new ToolError(`${label}.direction must be one of ${LOAD_DIRECTIONS.join(', ')}.`);

  const base = {
    guid: crypto.randomUUID(),
    definitionAxis: 'X' as const,
    loadDirection: direction,
    loadType: input.loadType,
    memberType: member,
    name: input.name ?? null,
  };

  let loadDefinition: ManualLoadDefinition;
  if (input.kind === 'point') {
    const at = requireFinite(input.at, `${label}.at`);
    const magnitude = requireFinite(input.magnitude, `${label}.magnitude`);
    if (magnitude <= 0) throw new ToolError(`${label}.magnitude must be more than 0; use direction for its sense.`);
    requireOnTruss(at, bounds, `${label}.at`);
    loadDefinition = { ...base, distribution: 'Concentrated', coordinate: at, magnitude };
  } else if (input.kind === 'distributed') {
    const from = requireFinite(input.from, `${label}.from`);
    const to = requireFinite(input.to, `${label}.to`);
    const startMagnitude = requireFinite(input.startMagnitude ?? input.magnitude, `${label}.startMagnitude`);
    const endMagnitude = requireFinite(input.endMagnitude ?? startMagnitude, `${label}.endMagnitude`);
    if (to <= from) throw new ToolError(`${label}.to must be to the right of from.`);
    if (startMagnitude <= 0 && endMagnitude <= 0) throw new ToolError(`${label} needs a magnitude more than 0.`);
    requireOnTruss(from, bounds, `${label}.from`);
    requireOnTruss(to, bounds, `${label}.to`);
    loadDefinition = {
      ...base,
      distribution: 'Distributed',
      startCoordinate: from,
      endCoordinate: to,
      startMagnitude,
      endMagnitude,
      magnitudeUnit: input.unit === 'psf' ? 'PoundsPerSquareFoot' : 'PoundsPerLinearFoot',
      isProjected: true,
    };
  } else {
    throw new ToolError(`${label}.kind must be point or distributed.`);
  }

  const loadCaseIdentification =
    input.loadCase != null
      ? AddLoadTo.SpecificLoadCase
      : input.stationaryOnly
        ? AddLoadTo.StationaryLoadCasesWithSpecifiedLoadType
        : AddLoadTo.LoadCasesWithSpecifiedLoadType;

  return {
    guid: crypto.randomUUID(),
    loadCaseIdentification,
    specifiedLoadCaseName: input.loadCase ?? null,
    load: { loadInsertionMethod: 'ProjectedCoordinates', loadDefinition },
  };
}

const LOAD_ID_LENGTH = 8;

/** A load's id, from its guid, so it stays the same when other loads are added or deleted. */
const loadId = (settings: ManualLoadSettings) => settings.guid.slice(0, LOAD_ID_LENGTH);

const loadName = (settings: ManualLoadSettings) =>
  settings.load.loadInsertionMethod === 'Points' ? settings.load.load.name : settings.load.loadDefinition.name;

const round = (value: number) => Math.round(value * 16) / 16;

/**
 * Describes a truss's manual loads: each one's id, where it is, how large, and which way it acts.
 */
export function describeManualLoads(componentDesign: ComponentDesign) {
  return (componentDesign.additionalLoads?.manualLoadSettings ?? []).map((settings) => {
    const id = loadId(settings);
    const cases =
      settings.loadCaseIdentification === AddLoadTo.SpecificLoadCase
        ? `load case ${settings.specifiedLoadCaseName}`
        : settings.loadCaseIdentification === AddLoadTo.StationaryLoadCasesWithSpecifiedLoadType
          ? 'stationary cases with its load type'
          : 'cases with its load type';

    // Drawn by clicking points on the truss: positions in the truss's coordinates, and a direction vector.
    if (settings.load.loadInsertionMethod === 'Points') {
      const { load } = settings.load;
      const point = (value: { x: number; y: number }) => ({ x: round(value.x), y: round(value.y) });
      return {
        id,
        name: load.name,
        loadType: load.loadType,
        member: load.memberType,
        direction: { x: round(load.direction.x * 1000) / 1000, y: round(load.direction.y * 1000) / 1000 },
        cases,
        ...(load.distribution === 'Concentrated'
          ? { kind: 'point', at: point(load.start), magnitude: `${load.startMagnitude} lb` }
          : {
              kind: 'distributed',
              from: point(load.start),
              to: point(load.end),
              magnitude: `${load.startMagnitude} to ${load.endMagnitude} ${
                load.magnitudeUnit === 'PoundsPerSquareFoot' ? 'psf' : 'plf'
              }`,
            }),
      };
    }

    const definition = settings.load.loadDefinition;
    return {
      id,
      name: definition.name,
      loadType: definition.loadType,
      member: definition.memberType,
      direction: definition.loadDirection,
      cases,
      ...(definition.distribution === 'Concentrated'
        ? { kind: 'point', at: definition.coordinate, magnitude: `${definition.magnitude} lb` }
        : {
            kind: 'distributed',
            from: definition.startCoordinate,
            to: definition.endCoordinate,
            magnitude: `${definition.startMagnitude} to ${definition.endMagnitude} ${
              definition.magnitudeUnit === 'PoundsPerSquareFoot' ? 'psf' : 'plf'
            }`,
          }),
    };
  });
}

function findLoad(componentDesign: ComponentDesign, label: string) {
  const loads = componentDesign.additionalLoads?.manualLoadSettings ?? [];
  const wanted = label.trim().toLowerCase();
  const matches = loads.filter(
    (settings) =>
      loadId(settings).toLowerCase() === wanted ||
      settings.guid.toLowerCase() === wanted ||
      loadName(settings)?.toLowerCase() === wanted,
  );
  if (matches.length === 0)
    throw new ToolError(
      `There is no load "${label}". The loads are ${
        loads.map((settings) => loadId(settings)).join(', ') || 'none'
      }; get_loads describes them.`,
    );
  if (matches.length > 1)
    throw new ToolError(`More than one load is named "${label}"; use their ids: ${matches.map(loadId).join(', ')}.`);
  return matches[0];
}

const TRUSS_SCHEMA = { type: 'string', description: 'The truss’s name.' };

export const loadTools = [
  defineTool<{ truss: string }>({
    name: 'get_loads',
    description:
      'Reads the loads the designer added to a truss, each with an id that doesn’t change, where it is, its ' +
      'magnitude and direction, and the truss’s load cases by the numbers analysis results use.',
    inputSchema: { type: 'object', properties: { truss: TRUSS_SCHEMA }, required: ['truss'] },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      return {
        summary: {
          truss: componentDesign.component.name,
          loads: describeManualLoads(componentDesign),
          loadCases: getLoadCasesForActiveComponentDesign(getState()).map((loadCase, index) => ({
            number: index + 1,
            name: loadCase.name,
          })),
        },
      };
    },
  }),

  defineTool<{ truss: string; loads: LoadInput[] }>({
    name: 'add_loads',
    description:
      'Adds point or distributed loads to a truss, as the Add Load panel does. Positions are x in inches from the ' +
      'left end of the bottom chord. A load joins every load case that has its load type (a Wind load goes in the ' +
      'wind cases); give loadCase to put it in one case instead. Point loads are in pounds; distributed loads in plf ' +
      'or psf. Run Upgrade and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        loads: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              kind: { type: 'string', enum: ['point', 'distributed'] },
              loadType: { type: 'string', enum: LOAD_TYPES },
              member: { type: 'string', enum: loadMemberTypes, description: 'Defaults to TopChord.' },
              at: { type: 'number', description: 'Point loads: x in inches.' },
              magnitude: { type: 'number', description: 'Point loads: pounds. Distributed loads: a uniform load.' },
              from: { type: 'number', description: 'Distributed loads: start x in inches.' },
              to: { type: 'number', description: 'Distributed loads: end x in inches.' },
              startMagnitude: { type: 'number' },
              endMagnitude: { type: 'number' },
              unit: { type: 'string', enum: ['plf', 'psf'], description: 'Distributed loads. Defaults to plf.' },
              direction: { type: 'string', enum: LOAD_DIRECTIONS, description: 'Defaults to GlobalDown.' },
              loadCase: { type: 'string', description: 'Put the load in this one load case only.' },
              stationaryOnly: { type: 'boolean', description: 'Only the stationary cases with its load type.' },
              name: { type: 'string' },
            },
            required: ['kind', 'loadType'],
          },
        },
      },
      required: ['truss', 'loads'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (!Array.isArray(input.loads) || input.loads.length === 0) throw new ToolError('Give at least one load.');
      if (componentDesign.additionalLoads == null) throw new ToolError('This truss can’t take added loads.');

      const bounds = calculateActiveComponentMembersMinMax(getState());
      if (bounds == null) throw new ToolError('Could not find the truss’s extents.');

      const loadCaseNames = getLoadCasesForActiveComponentDesignSorted(getState()).map((loadCase) => loadCase.name);
      for (const load of input.loads)
        if (load.loadCase != null && loadCaseNames.length > 0 && !loadCaseNames.includes(load.loadCase))
          throw new ToolError(`There is no load case "${load.loadCase}". The cases are ${loadCaseNames.join(', ')}.`);

      const settings = input.loads.map((load, index) => buildManualLoad(load, bounds, index));
      dispatch(addManualLoads(componentDesign, settings));
      await waitForAppToSettle(getState);

      const updated = getState().componentDesigns.data[componentDesign.guid];
      const added = new Set(settings.map((load) => load.guid));
      const saved = updated.additionalLoads?.manualLoadSettings ?? [];
      if (!saved.some((load) => added.has(load.guid))) throw new ToolError('The app didn’t add the loads.');
      return {
        summary: {
          truss: updated.component.name,
          added: saved.filter((load) => added.has(load.guid)).map(loadId),
          loads: describeManualLoads(updated),
        },
      };
    },
  }),

  defineTool<{ truss: string; loads: string[] }>({
    name: 'delete_loads',
    description: 'Deletes loads the designer added to a truss, by the ids or names get_loads returns.',
    inputSchema: {
      type: 'object',
      properties: { truss: TRUSS_SCHEMA, loads: { type: 'array', items: { type: 'string' } } },
      required: ['truss', 'loads'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const loads = requireStringArray(input, 'loads').map((label) => findLoad(componentDesign, label));

      await dispatch(deleteItems(loads.map((load) => ({ type: 'manualLoad' as const, guid: load.guid }))));
      await waitForAppToSettle(getState);

      const updated = getState().componentDesigns.data[componentDesign.guid];
      const remaining = new Set(updated.additionalLoads?.manualLoadSettings.map((settings) => settings.guid));
      if (loads.some((load) => remaining.has(load.guid))) throw new ToolError('The app could not delete those loads.');
      return { summary: { truss: updated.component.name, loads: describeManualLoads(updated) } };
    },
  }),
];
